import {StopIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash} from '@primer/react'
import {useId, useState} from 'react'

import {MergeBoxSectionHeader} from './common/MergeBoxSectionHeader'
import {ConflictingFilesList} from './conflicts/ConflictingFilesList'
import {ResolveConflictsButton} from './conflicts/ResolveConflictsButton'
import {UpdateBranchButton} from './conflicts/UpdateBranchButton'
import {CONFLICT_SECTION_STATUSES} from './conflicts/conflicts-section-presentation'
import type {AdvisoryWorkspace, ConflictsSectionStatuses, ViewerUpdateMethods} from '../../types'
import {useIsMergeBoxSidePanel} from '../../contexts/MergeBoxLayoutContext'

import type {ConflictMergeConditionPayload} from '../../page-data/payloads/merge-box'
import {useFeatureState} from '@github-ui/pull-requests/use-feature-state'
import {FeatureState} from '@github-ui/pull-requests/page-data/payloads/header'
import {GitDegradationAdvisory} from '../GitDegradationAdvisory'

export type ConflictsSectionProps = {
  conflictsState: ConflictsSectionStatuses
  baseRefName: string
  headRefOid: string
  resourcePath: string
  viewerCanUpdateBranch: boolean
  viewerLogin: string
  conflictsCondition: ConflictMergeConditionPayload
  canUserPushToBase: boolean
  viewerCanUseCopilotCodingAgent: boolean
  advisoryWorkspace: AdvisoryWorkspace
  viewerUpdateMethods: ViewerUpdateMethods | null
  stackState?: {isStacked: boolean}
  showGitDegradationAdvisory?: boolean
  showAgentMerge?: boolean
  pullRequestChannel?: string | null
}

/**
 *
 * Renders the pull request "git" merge status
 * If there are merge conflicts, describes them.
 * Allows the user to update a branch if it's out of date.
 * Also: It returns null if the merge state status is BLOCKED because this component doesn't care about BLOCKED.
 */
export function ConflictsSection({
  baseRefName,
  headRefOid,
  resourcePath,
  viewerCanUpdateBranch,
  viewerUpdateMethods,
  viewerLogin,
  conflictsCondition,
  conflictsState,
  canUserPushToBase,
  viewerCanUseCopilotCodingAgent,
  advisoryWorkspace,
  stackState,
  showGitDegradationAdvisory = false,
  showAgentMerge = false,
  pullRequestChannel,
}: ConflictsSectionProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [agentMergeError, setAgentMergeError] = useState<string | null>(null)
  const conflictsSectionAriaId = useId()
  const stacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const isSidePanel = useIsMergeBoxSidePanel()

  const conflictsWebEditorPath = `${resourcePath}/conflicts`
  const showAgentOption = canUserPushToBase && !!viewerCanUseCopilotCodingAgent && !isSidePanel

  const conflicts = conflictsCondition.conflicts ?? []

  const hasStack = stacksEnabled && stackState?.isStacked
  const showUpdateBranchButton =
    !hasStack &&
    (conflictsState === 'OUT_OF_DATE' || conflictsState === 'NO_CONFLICTS' || conflictsState === 'PENDING') &&
    viewerCanUpdateBranch

  // only list files if user can resolve conflict or user can push to head (aka has sufficient access)
  const shouldListConflictingFiles =
    conflictsState === 'HAS_CONFLICTS' &&
    (conflictsCondition.webEditorConflictResolution?.viewerCanResolve ||
      conflictsCondition.webEditorConflictResolution?.viewerCannotResolve?.reason !== 'INSUFFICIENT_ACCESS')

  const advisoryWorkspaceId = advisoryWorkspace?.advisoryWorkspaceId
  const advisoryWorkspacePath = advisoryWorkspace?.advisoryWorkspacePath

  return (
    <section
      aria-label="Conflicts"
      className="border-bottom borderColor-muted"
      aria-describedby={conflictsSectionAriaId}
    >
      <div className="d-flex flex-column width-full overflow-hidden">
        <MergeBoxSectionHeader
          headerId={conflictsSectionAriaId}
          icon={CONFLICT_SECTION_STATUSES[conflictsState].icon}
          title={<>{CONFLICT_SECTION_STATUSES[conflictsState].heading}</>}
          subtitle={
            showGitDegradationAdvisory && conflictsState === 'PENDING' ? (
              <GitDegradationAdvisory />
            ) : canUserPushToBase ? (
              hasStack && conflictsState === 'HAS_CONFLICTS' ? (
                'Use the command line to resolve conflicts within the stack before continuing.'
              ) : (
                CONFLICT_SECTION_STATUSES[conflictsState].subtitle({
                  baseRefName,
                  viewerLogin,
                  conflictsWebEditorPath,
                  conflictsCondition,
                  advisoryWorkspaceId,
                  advisoryWorkspacePath,
                })
              )
            ) : (
              'Changes can be cleanly merged.'
            )
          }
          rightSideContent={
            <>
              {showUpdateBranchButton && (
                <UpdateBranchButton
                  headRefOid={headRefOid}
                  onError={setErrorMessage}
                  viewerUpdateMethods={viewerUpdateMethods}
                />
              )}
              {!hasStack && (
                <ResolveConflictsButton
                  conflictsWebEditorPath={conflictsWebEditorPath}
                  conflictsState={conflictsState}
                  onError={setErrorMessage}
                  onAgentMergeError={setAgentMergeError}
                  showAgentOption={showAgentOption}
                  agentMerge={
                    showAgentMerge ? {currentHeadSha: headRefOid, viewerLogin, pullRequestChannel} : undefined
                  }
                  webEditorConflictResolution={conflictsCondition.webEditorConflictResolution}
                />
              )}
            </>
          }
        >
          {shouldListConflictingFiles && <ConflictingFilesList conflicts={conflicts} className="tmp-ml-n3" />}
        </MergeBoxSectionHeader>
        {errorMessage && (
          <Flash className="tmp-m-3" variant="danger">
            <StopIcon className="mr-2" />
            {errorMessage}
          </Flash>
        )}
        {agentMergeError && (
          <Flash className="tmp-m-3" variant="danger">
            <StopIcon className="mr-2" />
            {agentMergeError}
          </Flash>
        )}
      </div>
    </section>
  )
}
